import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoFull } from '../components/Logo';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { paymentsAPI } from '../services/api';

const Pricing = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const pricing = t.pricing || {};
  const footer = t.footer || {};

  const handleSubscribe = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?redirect=/pricing');
      return;
    }

    setLoading(true);
    try {
      const response = await paymentsAPI.createCheckout(billingCycle);
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartFree = () => {
    navigate('/signup');
  };

  // Pricing
  const monthlyPrice = '4,99€';
  const annualPrice = '39,99€';
  const savingsPercent = 33;

  // Free plan features
  const freeIncluded = [
    pricing.lessonsCount || "24 leçons (6 par cours)",
    pricing.interactiveQuestions || "Questions interactives",
    pricing.basicGamification || "Gamification de base",
    pricing.heartsPerDay || "5 cœurs par jour",
    pricing.publicLeaderboard || "Classement public",
  ];

  const freeLocked = [
    { icon: "👨‍👩‍👧‍👦", text: pricing.lockedFamilyMode || "Mode Famille (jusqu'à 4 profils)" },
    { icon: "🌍", text: pricing.lockedAllLanguages || "Accès à toutes les langues" },
    { icon: "🏆", text: pricing.lockedChallenges || "Challenges annuels & exclusifs" },
    { icon: "", text: pricing.lockedAdvancedLevels || "Niveaux avancés" },
    { icon: "", text: pricing.lockedOfflineMode || "Mode hors-ligne" },
  ];

  // Premium plan features
  const premiumFeatures = [
    { icon: "", text: pricing.everythingFree || "Tout du plan Gratuit" },
    { icon: "👨‍👩‍👧‍👦", text: pricing.familyMode || "Mode Famille (jusqu'à 4 profils)" },
    { icon: "🌍", text: pricing.allLanguages || "Accès à toutes les langues" },
    { icon: "📚", text: pricing.unlimitedAccess || "Accès illimité à tous les parcours" },
    { icon: "🏆", text: pricing.annualChallenges || "Classements & challenges annuels" },
    { icon: "❤️", text: pricing.unlimitedHearts || "Cœurs illimités" },
    { icon: "🎓", text: pricing.downloadCertificates || "Certificats téléchargeables" },
    { icon: "📶", text: pricing.offlineMode || "Mode hors-ligne" },
    { icon: "", text: pricing.prioritySupport || "Support prioritaire" },
  ];

  // Billing toggle labels
  const monthlyLabel = pricing.monthly || "Mensuel";
  const annualLabel = pricing.annual || "Annuel";

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer hover:opacity-80 transition-opacity">
              <LogoFull iconClassName="w-8 h-8" textClassName="text-xl" />
            </div>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <button 
                onClick={() => navigate('/login')}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                {pricing.login || "Se connecter"}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Headline */}
        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
            {pricing.title || "Apprenez les religions du monde, jouez en famille et pratiquez des langues."}
          </h1>
          <p className="mt-3 text-gray-500 text-base">
            {pricing.subtitle || "Une expérience ludique, culturelle et multilingue, pour tous les âges."}
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-6">
          <div className="bg-gray-100 p-1 rounded-full flex items-center">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {monthlyLabel}
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {annualLabel}
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-bold">
                -{savingsPercent}%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {/* FREE Card */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-lg">
            <div>
              <div className="text-sm font-bold text-gray-900">
                {pricing.free || "Gratuit"}
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-5xl font-black text-gray-900">0€</span>
                <span className="text-gray-500 text-base">{pricing.forever || "pour toujours"}</span>
              </div>
              <div className="mt-3 text-gray-500 text-sm">
                {pricing.freeDescription || "Découverte complète pour commencer."}
              </div>
            </div>

            <button
              onClick={handleStartFree}
              className="mt-4 w-full py-3 px-4 rounded-xl border border-gray-900 bg-gray-900 text-white font-bold hover:bg-gray-800 transition-colors"
            >
              {pricing.startFree || "Commencer gratuitement"}
            </button>

            <ul className="mt-5 space-y-2.5 text-sm text-gray-900">
              {freeIncluded.map((feature, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="text-green-500">✅</span> {feature}
                </li>
              ))}
              {freeLocked.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-400">
                  <span>🔒</span> {feature.icon && <span>{feature.icon}</span>} {feature.text}
                </li>
              ))}
            </ul>
          </div>

          {/* PREMIUM Card */}
          <div className="bg-white border-2 border-purple-600 rounded-2xl p-5 shadow-xl relative">
            {/* Badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold">
              ⭐ {pricing.mostPopular || "Le plus populaire"}
            </div>

            <div className="mt-1">
              <div className="flex items-center gap-2">
                <span className="text-lg">👑</span>
                <span className="text-sm font-extrabold text-gray-900">
                  {pricing.premium || "Premium"}
                </span>
              </div>

              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-5xl font-black text-purple-700">
                  {billingCycle === 'monthly' ? monthlyPrice : annualPrice}
                </span>
                <span className="text-gray-500 text-base">
                  {billingCycle === 'monthly' ? (pricing.perMonth || "par mois") : (pricing.perYear || "par an")}
                </span>
              </div>

              {billingCycle === 'annual' && (
                <div className="mt-1 text-green-600 text-sm font-medium">
                  {pricing.savePercent || `Économisez ${savingsPercent}%`}
                </div>
              )}

              <div className="mt-2 text-gray-500 text-sm font-bold">
                {pricing.premiumSubtitle || "Jusqu'à 4 profils inclus — idéal pour les familles."}
              </div>

              <div className="mt-3 text-gray-700 text-sm">
                {pricing.premiumDescription || "Débloquez l'expérience complète : apprentissage + langues + compétition."}
              </div>
            </div>

            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="mt-4 w-full py-3 px-4 rounded-xl bg-purple-600 text-white font-black hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {loading 
                ? (pricing.redirecting || "Redirection...")
                : (pricing.goPremium || "Débloquer l'expérience complète")
              }
            </button>

            <ul className="mt-5 space-y-2.5 text-sm text-gray-900">
              {premiumFeatures.map((feature, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="text-green-500">✅</span> 
                  {feature.icon && <span>{feature.icon}</span>} {feature.text}
                </li>
              ))}
            </ul>

            {/* Tip Box */}
            <div className="mt-4 p-3 rounded-xl bg-purple-50 border border-purple-100 text-purple-700 text-sm">
              🔥 {pricing.familyTip || "Astuce : lancez une compétition amicale en famille avec le classement interne (semaine / mois / streak)."}
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-5 text-gray-400 text-xs">
          {pricing.footerNote || "Spirify est une expérience ludique et respectueuse. Aucun prosélytisme."}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} Spirify. {footer.rights || "All rights reserved."}
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
